/**
 * Service-account heuristics, ported from dev-utils
 * `lib/harmony_utils/classify_service_accounts.py` so the page flags the same
 * accounts the CLI does. Two deliberate differences: the manager signal reads
 * `manager_name` (the CLI reads `manager`, which the employees API never
 * returns, so it scores every employee as unmanaged), and the devices and
 * integration-sources signals are left out because the employees list has no
 * such fields (the CLI adds their +5/+5 to every employee).
 */

/** The employees API fields the heuristics read. Mirrors employees Employee. */
export interface ClassifiableEmployee {
  id: string;
  work_email: string;
  type: "person" | "service_account";
  display_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  job_title?: string | null;
  department?: string | null;
  manager_name?: string | null;
}

export interface Classification {
  employee: ClassifiableEmployee;
  score: number;
  reasons: string[];
}

/** Same default cut-off as the CLI's --threshold. */
export const DEFAULT_THRESHOLD = 50;

const EMAIL_KEYWORDS = [
  "test",
  "bot",
  "service",
  "api",
  "noreply",
  "no-reply",
  "admin",
  "system",
  "automated",
  "automation",
  "integration",
];

const NAME_KEYWORDS = [
  "test",
  "bot",
  "service",
  "api",
  "system",
  "admin",
  "automated",
  "automation",
  "integration",
  "webhook",
];

export function classifyEmployee(employee: ClassifiableEmployee): Classification {
  let score = 0;
  const reasons: string[] = [];
  const add = (points: number, reason: string) => {
    score += points;
    reasons.push(`${reason} (+${points})`);
  };

  const email = employee.work_email.toLowerCase();
  const emailKeyword = EMAIL_KEYWORDS.find((keyword) => email.includes(keyword));
  if (emailKeyword) add(30, `Email contains '${emailKeyword}'`);
  if (email.split("@").at(-1)?.includes("test")) add(20, "Test domain");
  if (email.includes("#ext#") || email.includes("_ext_")) {
    add(20, "External account pattern");
  }

  const displayName = employee.display_name ?? "";
  const nameLower = displayName.toLowerCase();
  const nameKeyword = NAME_KEYWORDS.find((keyword) => nameLower.includes(keyword));
  if (nameKeyword) add(25, `Name contains '${nameKeyword}'`);
  if (displayName && !displayName.trim().includes(" ")) {
    add(15, "Single word name");
  }
  if (!employee.first_name || !employee.last_name) {
    add(10, "Missing first or last name");
  }

  if (!employee.manager_name) add(10, "No manager");
  if (!employee.department) add(10, "No department");
  if (!employee.job_title) add(10, "No job title");

  return { employee, score, reasons };
}

export type ConfidenceLevel = "high" | "medium-high" | "medium" | "low";

/** The CLI's confidence bands. */
export function confidenceLevel(score: number): ConfidenceLevel {
  if (score >= 80) return "high";
  if (score >= 65) return "medium-high";
  if (score >= 50) return "medium";
  return "low";
}

/** People scoring at or above the threshold, highest score first. */
export function detectServiceAccounts(
  employees: ClassifiableEmployee[],
  threshold: number,
): Classification[] {
  return employees
    .filter((employee) => employee.type !== "service_account")
    .map(classifyEmployee)
    .filter((result) => result.score >= threshold)
    .sort((a, b) => b.score - a.score);
}
